import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { getStoreValue } from '@frontend/sports/common/core/utils/redux';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { ConfirmationDialogButtonType, DialogAnimation } from '@frontend/sports/modal/feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { UserService } from '@frontend/sports/user/feature';
import { HtmlNode, LoadingIndicatorService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { EpcotConfigService } from 'packages/sports/web/app/src/common/epcot-config.service';
import { BaseUiChangeHandler } from 'packages/sports/web/app/src/ui-manager/base-ui-change-handler';
import { UiManagerService } from 'packages/sports/web/app/src/ui-manager/ui-manager.service';
import { IUiState } from 'packages/sports/web/app/src/ui-manager/ui-manager.state';
import { UiStates } from 'packages/sports/web/app/src/ui-manager/ui-states.enum';

import { IBetslipRootState } from '../../../base/store/state';
import { BetslipSourcePageActions } from '../../../core/betslip-source-page.actions';
import { SourcePageType } from '../../../model/edit-mybet/edit-bet-init';
import { EditBetActions } from '../actions';
import { BetslipSourcePageService } from './betslip-source-page.service';
import { EditBetAddSelectionInfoDialogService } from './dialogs/edit-bet-add-selection-info-dialog.service';
import { EditBetExitConfirmationDialogService } from './dialogs/edit-bet-exit-confirmation-dialog.service';
import { EditBetModalDialogService } from './edit-bet-modal-dialog.service';

type ClickItem = 'CLICK_ITEM';
type NavigateAwayItem = 'NAVIGATE_AWAY_ITEM';
type ElementType = NavigateAwayItem | ClickItem;

@Injectable()
export class EditBetUiChangeHandler extends BaseUiChangeHandler {
    static APP_IN_EDIT_BET_MODE_CLASS = 'app__edit-bet-mode';
    static APP_IN_EDIT_BET_ADD_SELECTION_CLASS = 'app__edit-bet-open-add-selection';
    static APP_IN_EDIT_BET_RESULT_MODE_CLASS = 'app__edit-bet__resulted-mode';

    private readonly _doc = inject(DOCUMENT);

    constructor(
        protected override uiManager: UiManagerService,
        private redirectHelper: RedirectHelperService,
        private userService: UserService,
        private htmlNodeService: HtmlNode,
        private editBetModalDialogService: EditBetModalDialogService,
        private editBetAddSelectionInfoDialogService: EditBetAddSelectionInfoDialogService,
        private timerService: TimerService,
        private loadingIndicatorService: LoadingIndicatorService,
        private store: Store<IBetslipRootState>,
        private betslipSourcePageService: BetslipSourcePageService,
        private epcotConfigService: EpcotConfigService,
        private editBetExitConfirmationDialogService: EditBetExitConfirmationDialogService,
    ) {
        super(uiManager);
    }

    updateUi(prev: IUiState, next: IUiState): void | Promise<any> {
        const currentlyInColumn = this.inState(next, UiStates.RightColumnVisible);
        const previouslyInColumn = this.inState(prev, UiStates.RightColumnVisible);
        const betslipState = getStoreValue(this.store).betslip;
        const delayToUpdateDefaultOptions = 2000;
        const delayToUpdateBetSelectionMessage = 1000;
        const delayToLoadEditBetModal = 5000;

        if (this.isStateRemoved(prev, next, UiStates.EditBet)) {
            this.removeNavigateAwayHandlers();
            this.removeEditMyBetClass();
        }

        if (this.isStateAdded(prev, next, UiStates.EditBet)) {
        }

        if (this.isStateAdded(prev, next, UiStates.EditBetResult)) {
            this.addEditMyBetResultClass();
        }

        if (this.isStateRemoved(prev, next, UiStates.EditBetResult)) {
            this.removeEditMyBetResultClass();
        }

        if (!currentlyInColumn) {
            if (!this.userService.isAuthenticated) {
                this.editBetModalDialogService.closeEditBetModal();

                return;
            }

            if (this.isStateAdded(prev, next, UiStates.EditBet)) {
                // Navigate to home screen so the user easily to add picks.
                const loadingService: any = this.loadingIndicatorService;
                const oldDelay = loadingService.defaultOptions.delay;
                if (this.epcotConfigService.isEnabled()) {
                    this.editBetModalDialogService.openEditBetModal(DialogAnimation.SlideInFromRight);
                } else {
                    this.editBetModalDialogService.openEditBetModal();
                    loadingService.defaultOptions.delay = delayToLoadEditBetModal;
                }
                this.redirectHelper.goHome();
                if (!this.epcotConfigService.isEnabled()) {
                    this.timerService.setTimeoutOutsideAngular(() => {
                        loadingService.defaultOptions.delay = oldDelay;
                    }, delayToUpdateDefaultOptions);
                }
            }

            if (this.isStateRemoved(prev, next, UiStates.EditBet)) {
                // Switch off edit my bet state.
                this.editBetModalDialogService.closeEditBetModal();

                const sourcePageState = betslipState.sourcePageState;
                this.betslipSourcePageService.applySourcePageState(sourcePageState);
            }

            if (this.isStateAdded(prev, next, UiStates.EditBetAddSelection)) {
                // Go to Add selection state.
                this.editBetModalDialogService.closeEditBetModal(false);
                this.addEditMyBetAddSelectionClass();
                this.editBetAddSelectionInfoDialogService.show();
            }

            if (this.isStateRemoved(prev, next, UiStates.EditBetAddSelection)) {
                // Edit bet selection mode is removed, still in edit bet mode - show open my bet modal.
                this.removeEditMyBetAddSelectionClass();

                if (this.inState(next, UiStates.EditBet)) {
                    if (this.epcotConfigService.isEnabled()) {
                        this.timerService.setTimeoutOutsideAngular(() => {
                            this.editBetModalDialogService.openEditBetModal(DialogAnimation.SlideInFromBottom);
                        }, delayToUpdateBetSelectionMessage); // Bet Selection\s added message purpose , we given timeout after 1000
                    } else {
                        this.editBetModalDialogService.openEditBetModal();
                    }
                    this.redirectHelper.goHome();
                }
            }

            if (this.isStateAdded(prev, next, UiStates.EditBetResult)) {
                const sourcePageState = betslipState.sourcePageState;
                this.betslipSourcePageService.applySourcePageState(sourcePageState, true);
            }
        }

        if (!previouslyInColumn && currentlyInColumn && this.inState(next, UiStates.EditBet)) {
            // Going from non right column to right column state while in edit bet mode
            if (!this.inState(next, UiStates.EditBetAddSelection)) {
                this.editBetModalDialogService.closeEditBetModal();
            }
        }

        if (previouslyInColumn && !currentlyInColumn && this.inState(next, UiStates.EditBet)) {
            // Going from right column to non right column state while in edit bet mode
            if (!this.inState(next, UiStates.EditBetAddSelection)) {
                this.editBetModalDialogService.openEditBetModal();
            }
        }

        if (this.isStateAdded(prev, next, UiStates.EditBet)) {
            // App goes in Edit Bet Mode.
            this.addEditMyBetClass();
            this.addNavigateAwayHandlers();
        }
    }

    /**
     * Add a class to HTML element so to modify app for edit bet mode.
     */
    private addEditMyBetClass(): void {
        this.htmlNodeService.setCssClass(EditBetUiChangeHandler.APP_IN_EDIT_BET_MODE_CLASS, true);
    }

    /**
     * Remove edit bet mode class from HTML element.
     */
    private removeEditMyBetClass(): void {
        this.htmlNodeService.setCssClass(EditBetUiChangeHandler.APP_IN_EDIT_BET_MODE_CLASS, false);
    }

    /**
     * Add a class to HTML element that currently in add selection mode.
     */
    private addEditMyBetAddSelectionClass(): void {
        this.htmlNodeService.setCssClass(EditBetUiChangeHandler.APP_IN_EDIT_BET_ADD_SELECTION_CLASS, true);
    }

    /**
     * Remove edit bet mode class from HTML element that currently in add selection mode
     */
    private removeEditMyBetAddSelectionClass(): void {
        this.htmlNodeService.setCssClass(EditBetUiChangeHandler.APP_IN_EDIT_BET_ADD_SELECTION_CLASS, false);
    }

    /**
     * Add a class to HTML element so to update the app for edit bet result mode.
     */
    private addEditMyBetResultClass(): void {
        this.htmlNodeService.setCssClass(EditBetUiChangeHandler.APP_IN_EDIT_BET_RESULT_MODE_CLASS, true);
    }

    /**
     * Remove edit bet result mode class from HTML element.
     */
    private removeEditMyBetResultClass(): void {
        this.htmlNodeService.setCssClass(EditBetUiChangeHandler.APP_IN_EDIT_BET_RESULT_MODE_CLASS, false);
    }

    private findParent = (el: HTMLElement, selector: (el: HTMLElement) => boolean, limit: number = 5): HTMLElement | null => {
        if (selector(el)) {
            return el;
        }
        let parent = el.parentElement;
        let limitCounter = 0;
        while (!!parent && limitCounter < limit) {
            if (selector(parent)) {
                return parent;
            }
            parent = parent.parentElement;
            limitCounter++;
        }

        return null;
    };

    private isEditBetIsPlaced = () => {
        return !!this._doc.querySelector('.betslip-edit-bet_screen__result');
    };

    /**
     * Checks if element tries to navigate the user away of sports app.
     *
     * @param el - element clicked.
     */
    private checkElementType = (el: HTMLElement): ElementType | null => {
        const sportsUrlTester = /http(s)?:\/\/(?!virtual)[^\/]*sports/;
        const isUrlPopup = /http(s)?:\/\/(.+)?popup=(.+)/;
        const sportsNav = this._doc.getElementById('sports-nav');
        if (sportsNav && this.findParent(el, (p) => p === sportsNav)) {
            const anchor = this.findParent(el, (p) => 'href' in p) as HTMLLinkElement;
            if (anchor) {
                const href = anchor.href;
                if (href && href.startsWith('http') && !sportsUrlTester.test(href)) {
                    return 'NAVIGATE_AWAY_ITEM';
                }
            }

            return null;
        }
        const iconRow = this._doc.querySelector('header .icon-row, .navbar-logo-wrapper');
        if (iconRow && this.findParent(el, (p) => p === iconRow)) {
            const state = getStoreValue(this.store).ui;
            if (this.inState(state, UiStates.EditBetAddSelection)) {
                if (el.classList.contains('brand-logo') || el.classList.contains('navbar-logo-wrapper')) {
                    return null;
                }
            }

            if (this.inState(state, UiStates.EditBetResult)) {
                return 'CLICK_ITEM';
            }

            return 'NAVIGATE_AWAY_ITEM';
        }
        const avatar = this._doc.querySelector('header .h-avatar');
        if (avatar && this.findParent(el, (p) => p === avatar)) {
            return 'CLICK_ITEM';
        }
        const balance = this._doc.querySelector('header .h-balance');
        if (avatar && this.findParent(el, (p) => p === balance)) {
            return 'CLICK_ITEM';
        }
        const deposit = this._doc.querySelector('header .header-btn');
        if (avatar && this.findParent(el, (p) => p === deposit)) {
            return 'NAVIGATE_AWAY_ITEM';
        }

        const bonusBalance = this._doc.querySelector('header .bonus-link');
        if (this.findParent(el, (p) => p === bonusBalance)) {
            return 'CLICK_ITEM';
        }

        const bottomNav = this._doc.querySelector('.bottom-nav');
        if (bottomNav && this.findParent(el, (p) => p === bottomNav)) {
            const anchor = this.findParent(el, (p) => p.tagName === 'A') as HTMLLinkElement;
            if (anchor) {
                const href = anchor.href;

                if (!href || !sportsUrlTester.test(href) || isUrlPopup.test(href)) {
                    // If bottom nav link does not contain href, leads outside of page, or is a popup then it is nav away.
                    return 'CLICK_ITEM';
                }
            }

            return null;
        }
        const footer = this._doc.querySelector('footer');
        if (footer && this.findParent(el, (p) => p === footer)) {
            return 'NAVIGATE_AWAY_ITEM';
        }
        const teaser = this._doc.getElementById('teasers');
        if (teaser && this.findParent(el, (p) => p === teaser)) {
            return 'NAVIGATE_AWAY_ITEM';
        }
        const banner = this._doc.querySelector('.banner-jackpot');
        if (banner && this.findParent(el, (p) => p === banner)) {
            return 'NAVIGATE_AWAY_ITEM';
        }
        const possibleParentAnchor = this.findParent(el, (p) => p.tagName === 'A' && 'href' in p) as HTMLLinkElement;
        if (possibleParentAnchor && possibleParentAnchor.href && !sportsUrlTester.test(possibleParentAnchor.href)) {
            return 'NAVIGATE_AWAY_ITEM';
        }

        return null;
    };

    private exitEditBet = () => {
        this.removeNavigateAwayHandlers(); // Call it so to be 100% sure that it removes the handlers.
        this.store.dispatch(EditBetActions.exitEditBet());
    };

    private onHeaderClickWhenInAddPick = async (event: Event) => {
        const target = event.target as HTMLElement;
        const targetElementType = this.checkElementType(target);

        if (targetElementType) {
            const anchor = this.findParent(target, (p) => 'href' in p) as HTMLLinkElement;

            if (this.isEditBetIsPlaced()) {
                // If edit bet is placed then exit edit bet.
                this.exitEditBet();
                if (anchor && anchor.href) {
                    this.redirectHelper.goToPage(anchor.href);
                }
            } else {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();

                const result = await this.editBetExitConfirmationDialogService.show();

                if (result !== ConfirmationDialogButtonType.Cancel) {
                    return;
                }

                this.store.dispatch(
                    BetslipSourcePageActions.setSourcePage({
                        payload: {
                            pageType: SourcePageType.None,
                            options: null,
                        },
                    }),
                );

                this.exitEditBet();

                if (anchor && anchor.href) {
                    this.redirectHelper.goToPage(anchor.href);

                    return;
                }

                if (targetElementType === 'CLICK_ITEM') {
                    target.click();

                    return;
                }

                target.dispatchEvent(event);
            }
        }
    };

    /**
     * Adds global document click to check if user tries to navigate away of sports app
     */
    private addNavigateAwayHandlers(): void {
        this._doc.addEventListener('touchstart', this.onHeaderClickWhenInAddPick, {
            capture: true,
            once: false,
            passive: false,
        });
        this._doc.addEventListener('mousedown', this.onHeaderClickWhenInAddPick, {
            capture: true,
            once: false,
            passive: false,
        });
    }

    /**
     * Removes the global document click.
     */
    private removeNavigateAwayHandlers(): void {
        this._doc.removeEventListener('touchstart', this.onHeaderClickWhenInAddPick, { capture: true });
        this._doc.removeEventListener('mousedown', this.onHeaderClickWhenInAddPick, { capture: true });
    }
}
